"use client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch, apiUrl } from "@/lib/api-client";
import { isBloomDoctorAvatarApiPath } from "@/lib/doctor-avatar";
function keepPreviousData(previous) {
    return previous;
}
export const avatarQueryKey = (doctorId) => ["doctor", "avatar", doctorId];
/** Кеш подписанного URL по doctorId. Его можно отдавать в <img src> без CORS. */
const urlByDoctorId = new Map();
export function invalidateDoctorAvatarCache(doctorId) {
    urlByDoctorId.delete(doctorId);
}
export function invalidateAndRefetchDoctorAvatar(queryClient, doctorId) {
    invalidateDoctorAvatarCache(doctorId);
    return queryClient.invalidateQueries({ queryKey: avatarQueryKey(doctorId) });
}
function cacheAvatarUrl(doctorId, url) {
    urlByDoctorId.set(doctorId, url);
    return url;
}
function isUsableImgUrl(url) {
    return url.length > 0 && !isBloomDoctorAvatarApiPath(url);
}
async function fetchSignedUrlFromFiles(doctorId, token) {
    var _a, _b;
    const res = await apiFetch(`/api/doctors/${doctorId}/files?file_type=avatar`, { token });
    if (!res.ok)
        return null;
    const data = (await res.json());
    const url = (_b = (_a = data.files) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.url;
    if (typeof url !== "string" || !isUsableImgUrl(url))
        return null;
    return url;
}
async function fetchSignedUrlFromAvatarRedirect(doctorId, token) {
    var _a, _b;
    const res = await apiFetch(`/v1/doctors/${doctorId}/avatar`, {
        token,
        redirect: "manual",
    });
    const location = (_a = res.headers.get("Location")) !== null && _a !== void 0 ? _a : res.headers.get("location");
    if (location) {
        const absolute = new URL(location, apiUrl(`/v1/doctors/${doctorId}/avatar`))
            .href;
        return isUsableImgUrl(absolute) ? absolute : null;
    }
    if (res.type === "opaqueredirect" ||
        (res.status >= 300 && res.status < 400)) {
        return null;
    }
    if (!res.ok)
        return null;
    const contentType = (_b = res.headers.get("content-type")) !== null && _b !== void 0 ? _b : "";
    if (!contentType.startsWith("image/") &&
        contentType !== "application/octet-stream") {
        return null;
    }
    const blob = await res.blob();
    if (!blob.size)
        return null;
    return URL.createObjectURL(blob);
}
/**
 * Подписанный URL для <img src>. Не fetch()'им объект из R2/S3 — у бакета нет CORS,
 * а тег img его не требует.
 */
export async function fetchDoctorAvatarDisplayUrl(doctorId, token) {
    const cached = urlByDoctorId.get(doctorId);
    if (cached)
        return cached;
    const fromFiles = await fetchSignedUrlFromFiles(doctorId, token).catch(() => null);
    if (fromFiles)
        return cacheAvatarUrl(doctorId, fromFiles);
    const fromRedirect = await fetchSignedUrlFromAvatarRedirect(doctorId, token).catch(() => null);
    if (fromRedirect)
        return cacheAvatarUrl(doctorId, fromRedirect);
    return undefined;
}
function resolveInitialDisplayUrl(doctorId, initialUrl) {
    if (doctorId) {
        const cachedUrl = urlByDoctorId.get(doctorId);
        if (cachedUrl)
            return cachedUrl;
    }
    if (!initialUrl || isBloomDoctorAvatarApiPath(initialUrl)) {
        return null;
    }
    return initialUrl;
}
export function useDoctorAvatarUrl(doctorId, options = {}) {
    var _a, _b;
    const { initialUrl, enabled = true } = options;
    const { token } = useAuth();
    const query = useQuery({
        queryKey: avatarQueryKey(doctorId !== null && doctorId !== void 0 ? doctorId : ""),
        queryFn: async () => {
            const url = await fetchDoctorAvatarDisplayUrl(doctorId, token);
            return url !== null && url !== void 0 ? url : null;
        },
        enabled: enabled && !!token && !!doctorId,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        placeholderData: keepPreviousData,
    });
    const data = (_b = (_a = query.data) !== null && _a !== void 0 ? _a : resolveInitialDisplayUrl(doctorId, initialUrl)) !== null && _b !== void 0 ? _b : null;
    return Object.assign(Object.assign({}, query), { data });
}
